// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { NOT_SET_SENTINEL } from '@/customer/constants/not-set-sentinel';
import { useCustomers } from '@/customer/hooks/use-customers';
import {
  BaseSearchableFilter,
  SearchableFilterOption,
} from '@/filters/base-searchable-filter';
import { useMemo } from 'react';

const DEFAULT_LABEL = 'Company';

interface Props {
  /**
   * Currently selected identifiers (customer `id` by default, or `key` when
   * `identifier="key"`). Pass `NOT_SET_SENTINEL` in the array to keep the
   * "Not set" sentinel active when `includeNotSet` is on.
   */
  value: string[];

  /**
   * The label for the filter menu.
   *
   * @default "Company"
   */
  label?: string;

  /**
   * A callback fired when the selected identifiers change.
   */
  onChange: (value: string[]) => void;

  /**
   * Whether to show the search input. Defaults to true.
   */
  enableSearch?: boolean;

  includeOwnOrganization?: boolean;

  /**
   * When true, restrict the fetched company list to records flagged as
   * customers (`isCustomer === true`). Defaults to false, which shows all
   * companies the organization has on file.
   */
  onlyCustomers?: boolean;

  /**
   * When true, prepend a "Not set" sentinel option so callers can filter for
   * rows that have no customer attached. The sentinel uses
   * `NOT_SET_SENTINEL` as its id and renders above the regular list with a
   * divider (via `BaseSearchableFilter`'s `isSpecial` rendering).
   */
  includeNotSet?: boolean;

  /**
   * Which field of the customer record to use as the option identifier.
   * Defaults to `id` (UUID) to preserve the existing callers' behavior.
   * Pass `key` when the caller stores customer keys (e.g. the charges list
   * URL state uses `customerKeys`).
   */
  identifier?: 'id' | 'key';
}

/**
 * A searchable, virtualized filter for selecting zero or more companies
 * from the remote API. Optionally restricts the list to customer-flagged
 * records, adds a "Not set" sentinel, and lets callers pick id vs key as
 * the option identifier.
 */
export function CustomerFilterMenu(props: Props) {
  const identifier = props.identifier ?? 'id';
  const { customers, isLoading } = useCustomers({
    filter: props.onlyCustomers ? { isCustomer: true } : undefined,
  });
  const { user } = useCurrentUser();

  const label = props.label ?? DEFAULT_LABEL;

  // Memoized on the customers: the options no longer carry selection
  const options: SearchableFilterOption[] = useMemo(() => {
    const customerOptions: SearchableFilterOption[] = customers.map(
      (customer) => ({
        value: identifier === 'key' ? customer.key : customer.id,
        label: customer.name,
      })
    );

    if (props.includeOwnOrganization) {
      customerOptions.unshift({
        value: 'ORGANIZATION',
        label: user.organization.name,
      });
    }

    if (props.includeNotSet) {
      customerOptions.unshift({
        value: NOT_SET_SENTINEL,
        label: 'Not set',
        isSpecial: true,
      });
    }

    return customerOptions;
  }, [
    customers,
    identifier,
    props.includeOwnOrganization,
    props.includeNotSet,
    user.organization.name,
  ]);

  const handleChangeOption = (id: string, isActive: boolean) => {
    if (isActive) {
      return props.onChange([...props.value, id]);
    }

    props.onChange(props.value.filter((entry) => entry !== id));
  };

  return (
    <BaseSearchableFilter
      label={label}
      options={options}
      selectedValues={props.value}
      onChangeOption={handleChangeOption}
      searchPlaceholder="Search companies..."
      enableSearch={props.enableSearch}
      isLoading={isLoading}
    />
  );
}
